import React, { memo, useMemo } from "react";
import { Avatar } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { FaCopy } from "react-icons/fa";
import { toast } from "sonner";
import { Profile } from "../../../types/types";
import { formatText } from "../../../utils/formatUtils";
import { DOMAIN_URL } from "../../../services/api";
import SocialMediaIcon from "./socialMediaIcon";
import Field from "./field";
import { StarIcon } from "@heroicons/react/24/solid";

interface BasicDetailsProps {
  member: Profile;
  showCopyButton?: boolean;
}

const userJson = localStorage.getItem("loginResponse");
const profileType = userJson ? JSON.parse(userJson).profileType : null;

const BasicDetails: React.FC<BasicDetailsProps> = ({
  member,
  showCopyButton = false,
}) => {
  const { t } = useTranslation("publicProfile");

  interface Stat {
    wins: number;
    losses: number;
    ko: number;
    tko: number;
    submission: number;
    decision: number;
    draw: number;
    dq: number;
    noContest: number;
    totalFights: number;
  }

  const fighterData = member.fighter?.fighterScoreResponseDto;

  const BreakdownBar = memo(
    ({
      label,
      value,
      total,
      color,
    }: {
      label: string;
      value: number;
      total: number;
      color: string;
    }) => {
      const percentage = useMemo(() => {
        if (total <= 0) return 0;
        return Math.round((value / total) * 100);
      }, [value, total]);

      return (
        <div className="mb-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-gray-600 text-xs font-medium">{label}</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2">
            <div
              className={`h-2 rounded-full ${color}`}
              style={{ width: `${percentage}%` }}
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
          <div className="text-right text-gray-500 text-xs mt-1">
            {percentage}%
          </div>
        </div>
      );
    }
  );

  const EmptyState = memo(({ message }: { message: string }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm text-center py-8">
      <div className="text-gray-500 text-sm">{message}</div>
    </div>
  ));

  const DetailedStats = memo(
    ({ titleKey, stats }: { titleKey: string; stats?: Stat }) => {
      if (!stats) return <EmptyState message={t(`no_${titleKey}_scores`)} />;

      const totalFightsForBars =
        stats.ko +
        stats.tko +
        stats.submission +
        stats.decision +
        stats.draw +
        stats.dq +
        stats.noContest;

      return (
        <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm transition hover:shadow-md max-w-md ml-0">
          <h4 className="text-gray-900 text-base font-semibold mb-4 border-b border-gray-200 pb-2 mr-15">
            {t(titleKey)}
          </h4>

          <div className="flex items-center space-x-6 mb-6">
            <div className="text-center">
              <div className="bg-green-100 text-green-700 text-sm font-semibold rounded px-3 py-1">
                {stats.wins}
              </div>
              <div className="text-green-600 text-xs uppercase mt-1">
                {t("wins")}
              </div>
            </div>
            <div className="text-gray-300 text-lg font-bold">/</div>
            <div className="text-center">
              <div className="bg-red-100 text-red-700 text-sm font-semibold rounded px-3 py-1">
                {stats.losses}
              </div>
              <div className="text-red-600 text-xs uppercase mt-1">
                {t("losses")}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <BreakdownBar
                label={t("ko")}
                value={stats.ko}
                total={totalFightsForBars}
                color="bg-orange-400"
              />
              <BreakdownBar
                label={t("tko")}
                value={stats.tko}
                total={totalFightsForBars}
                color="bg-orange-300"
              />
              <BreakdownBar
                label={t("submission")}
                value={stats.submission}
                total={totalFightsForBars}
                color="bg-purple-400"
              />
              <BreakdownBar
                label={t("decision")}
                value={stats.decision}
                total={totalFightsForBars}
                color="bg-blue-400"
              />
            </div>
            <div>
              <BreakdownBar
                label={t("draw")}
                value={stats.draw}
                total={totalFightsForBars}
                color="bg-gray-400"
              />
              <BreakdownBar
                label={t("dq")}
                value={stats.dq}
                total={totalFightsForBars}
                color="bg-red-400"
              />
              <BreakdownBar
                label={t("no_contest")}
                value={stats.noContest}
                total={totalFightsForBars}
                color="bg-yellow-400"
              />
              <div className="flex justify-between items-center p-2 rounded bg-blue-50">
                <span className="text-gray-900 font-medium text-xs">
                  {t("total_fights")}
                </span>
                <span className="text-gray-900 font-semibold text-sm">
                  {stats.totalFights}
                </span>
              </div>
            </div>
          </div>
        </div>
      );
    }
  );

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="relative">
            <div className="flex flex-col items-center">
              {/* Avatar */}
              <div className="relative w-20 h-20">
                {member.profileImage?.trim() ? (
                  <Avatar
                    src={member.profileImage}
                    alt={`${member.firstName} ${member.lastName}`}
                    className="w-20 h-20 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center text-lg font-semibold shadow-md">
                    {`${member.firstName?.[0]?.toUpperCase() || ""}${
                      member.lastName?.[0]?.toUpperCase() || ""
                    }`}
                  </div>
                )}

                {member.verified && (
                  <span className="absolute -bottom-0 -right-0 bg-white w-5 h-5 rounded-full overflow-hidden">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 48 48"
                      className="w-full h-full"
                    >
                      <polygon
                        fill="#3b82f6"
                        points="29.62,3 33.053,8.308 39.367,8.624 39.686,14.937 44.997,18.367 42.116,23.995 45,29.62 39.692,33.053 39.376,39.367 33.063,39.686 29.633,44.997 24.005,42.116 18.38,45 14.947,39.692 8.633,39.376 8.314,33.063 3.003,29.633 5.884,24.005 3,18.38 8.308,14.947 8.624,8.633 14.937,8.314 18.367,3.003 23.995,5.884"
                      />
                      <polygon
                        fill="#fff"
                        points="21.396,31.255 14.899,24.76 17.021,22.639 21.428,27.046 30.996,17.772 33.084,19.926"
                      />
                    </svg>
                  </span>
                )}
              </div>

              {member.rating != null && (
                <div className="flex flex-col items-center mt-2">
                  <span className="text-[13px] text-gray-600">
                    {t("overall_rating")}
                  </span>
                  <span className="text-[15px] font-medium text-black-500">
                    {member.rating.toFixed(1)}
                  </span>
                </div>
              )}

              {member.rating != null && (
                <div className="flex justify-center items-center gap-1 mt-1">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon
                      key={i}
                      className={`w-5 h-5 ${
                        i < Math.round(Number(member.rating))
                          ? "fill-yellow-400 text-yellow-400"
                          : "fill-gray-200 text-gray-300"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h3 className="text-start text-xl font-bold text-gray-900 flex items-center gap-1">
                {formatText(member.firstName)} {formatText(member.lastName)}
              </h3>
              {showCopyButton && (
                <div className="relative group">
                  <button
                    onClick={() => {
                      const profileUrl = `${DOMAIN_URL}/view-profile?profileId=${member.id}`;
                      navigator.clipboard
                        .writeText(profileUrl)
                        .then(() =>
                          toast.success(t("profile_copied_to_clipboard"))
                        )
                        .catch(() => toast.error(t("failed_to_copy_profile")));
                    }}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
                  >
                    <FaCopy className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 text-xs text-white bg-gray-800 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                    {t("copy_profile_link")}
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              )}
            </div>
            <p className="text-start text-gray-600">{member.username}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {(
            member.fighter?.socialMediaLinks ||
            member.manager?.socialMediaLinks ||
            []
          ).map((link: any, index: any) => (
            <SocialMediaIcon
              key={index}
              type={link.socialMediaAccountType}
              url={link.socialMediaAccountUrl}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 text-start">
        <Field label="nationality" value={member.nationality} />
        <Field label="residence_country" value={member.residenceCountry} />
        {member.phone && <Field label="phone" value={member.phone} />}
        <Field label="profile_status" value={member.profileStatus} />
        <Field label="verified" value={member.verified ? "Yes" : "No"} />
        {member.profileType === "ORGANIZATION" && (
          <Field
            label="organization_name"
            value={member.manager?.organizationName || "N/A"}
          />
        )}
        <Field label="gender" value={member.gender} />
        <Field label="bio" value={member.bio} />
      </div>

      {fighterData && (
        <div >
          <h3 className="text-xl font-semibold text-gray-900 border-b border-gray-200 pb-3 text-left mt-1">
            {t("fight_scores")}
          </h3>

          <div className="flex gap-6 mt-1 items-start">
            <div className="flex-1 text-left">
              <DetailedStats titleKey="amateur" stats={fighterData.amateur} />
            </div>

            <div className="flex-1 text-left">
              <DetailedStats
                titleKey="professional"
                stats={fighterData.professional}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicDetails;
